import { UI } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import React from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useManagementAnalysisActions } from '../../../../store/managementAnalysis/actions';
import { selectedFinancialMovementYearState } from '../../../../store/managementAnalysis/atom';
import { financialMovementDataQuery } from '../../../../store/managementAnalysis/selector';
import { mountChartMobileLegend } from '../../constants';

const financeItems: {
  dataKey: string;
  color: ColorTypes.Option;
  positive: boolean;
  label: string;
}[] = [
  {
    color: 'feedback.success.default',
    label: 'CASH-IN',
    dataKey: 'monthlyCashIn',
    positive: false,
  },
  { color: 'feedback.error.default', label: 'CASH-OUT', dataKey: 'monthlyCashOut', positive: true },
];

export declare namespace FinancialMovementChartProps {
  interface Props {
    yearsLoading: boolean;
    yearsError?: boolean;
    years?: string[];
    isMobile?: boolean;
  }
}

export const FinancialMovementChart: React.FC<FinancialMovementChartProps.Props> = ({
  years,
  yearsLoading,
  yearsError,
  isMobile,
}) => {
  const { setSelectedFinancialMovementYear } = useManagementAnalysisActions();
  const { data: yearSelected } = useRecoilQuery(selectedFinancialMovementYearState);
  const {
    loading: dataLoading,
    error: dataError,
    data,
    refresher,
  } = useRecoilQuery(financialMovementDataQuery);

  const loading = yearsLoading || dataLoading;
  const hasError = yearsError || dataError;

  return (
    <UI.Chart
      isMobile={isMobile}
      error={!!hasError}
      loading={loading}
      yearOptions={years}
      yearSelected={yearSelected}
      mobileLegend={mountChartMobileLegend(data?.financialMovement)}
      chartTitle="MOVIMENTAÇÕES FINANCEIRAS"
      updatedInText="Dados atualizados em D-1"
      legendInfo={[
        {
          title: 'CASH-IN/CASH-OUT',
          value: data?.information.netCashFormatted || '--',
          tooltip: 'O valor informado é referente à soma entre o cash-in e cash-out',
        },
      ]}
      legendItems={[
        { color: 'feedback.success.default', label: 'CASH-IN' },
        { color: 'feedback.error.default', label: 'CASH-OUT' },
      ]}
      onReloadChart={() => refresher?.()}
      onYearChange={setSelectedFinancialMovementYear}
      chartComponent={
        <UI.FinancialMovementChart
          isMobile={isMobile}
          data={data?.financialMovement}
          additionalData={{ year: parseInt(yearSelected, 10), tooltipLabels: financeItems }}
          bars={financeItems}
        />
      }
    />
  );
};
